import { Injectable } from '@angular/core';
import { ErrorCodes } from '@shared';
import { LoggingService } from '@shared/logging';
import { RxCollection, RxDatabase, RxDocument } from 'rxdb';
import { first, map, Observable } from 'rxjs';
import { CentralDatabaseProvider } from '../central-database.provider';
import { ApiConnectionsModel } from '../models';
import { ApiConnectionSelectedOptions } from '../models/api-connections-selected-options.model';
import { CentralDatabaseDbContext } from '../schemas';
import { CentralDatabaseKeys } from '../schemas/central-database-keys';
@Injectable()
export class ApiConnectionsRepository {
	private centralDb: RxDatabase<CentralDatabaseDbContext>;
	private transferConnectionsCollection: RxCollection<ApiConnectionsModel>;

	public constructor(
		private centralDbProvider: CentralDatabaseProvider,
		private loggingService: LoggingService
	) {
		this.centralDb = this.centralDbProvider.getDatabase();
		this.transferConnectionsCollection = this.centralDb[CentralDatabaseKeys.apiConnections];
	}

	public async insert(connectionData: ApiConnectionsModel): Promise<void> {
		try {
			await this.transferConnectionsCollection.insert(connectionData);
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionInsert, error);
			throw new Error('Insert transfer connection record failed');
		}
	}

	public async updateConnectionSelectedOptions(connectionId: string, options: ApiConnectionSelectedOptions) {
		try {
			await this.transferConnectionsCollection
				.findOne()
				.where(CentralDatabaseKeys.apiConnections_connectionId)
				.eq(connectionId)
				.update({
					$set: {
						[CentralDatabaseKeys.apiConnections_selectedOptions]: options
					}
				});
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionUpdateOptions, error);
			throw new Error('Update api connection selected options failed');
		}
	}

	public async updateConnectionAliasAndPath(
		connectionId: string,
		connectionAlias: string,
		apiKey: string,
		connectionPath: string
	) {
		try {
			await this.transferConnectionsCollection
				.findOne()
				.where(CentralDatabaseKeys.apiConnections_connectionId)
				.eq(connectionId)
				.update({
					$set: {
						[CentralDatabaseKeys.apiConnections_serverAlias]: connectionAlias,
						[CentralDatabaseKeys.apiConnections_apiKey]: apiKey,
						[CentralDatabaseKeys.apiConnections_serverPath]: connectionPath
					}
				});
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionUpdateAliasPath, error);
			throw new Error('Update api connection alias and path failed');
		}
	}

	public getConnectionDetails$(connectionId: string): Observable<ApiConnectionsModel | null> {
		try {
			return this.transferConnectionsCollection
				.findOne()
				.where(CentralDatabaseKeys.apiConnections_connectionId)
				.eq(connectionId)
				.$.pipe(
					map((transferConnectionsData: RxDocument<ApiConnectionsModel>) =>
						transferConnectionsData ? transferConnectionsData.toJSON() : null
					),
					first()
				);
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionDetails, error);
			throw new Error('Could not retrieve connection details');
		}
	}

	public getConnections$(): Observable<ApiConnectionsModel[] | null> {
		try {
			return this.transferConnectionsCollection
				.find()
				.$.pipe(
					map((transferConnectionsData: RxDocument<ApiConnectionsModel>[]) =>
						transferConnectionsData
							? transferConnectionsData.map((transferConnectionsItem: RxDocument<ApiConnectionsModel>) =>
									transferConnectionsItem.toJSON()
							  )
							: null
					)
				);
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionGetConnections, error);
			throw new Error('Could not retrieve list of connections.');
		}
	}

	public async deleteConnection(connectionId: string): Promise<boolean> {
		try {
			const connection = this.transferConnectionsCollection
				.find()
				.where(CentralDatabaseKeys.apiConnections_connectionId)
				.in([connectionId])
				.limit(1)
				.remove();

			return (await connection).length > 0 ? true : false;
		} catch (error) {
			this.loggingService.error(ErrorCodes.ApiConnectionDelete, error);
			throw new Error('Delete api connection failed');
		}
	}
}
